import { Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'

import { SuppressedValue } from '@/components/data/SuppressedValue'
import { Button } from '@/components/ui/Button'
import { RateMemoryChip } from '@/components/ui/Tag'
import {
  CostLineInlineRow,
  type CostLineInlineDraft,
} from '@/features/quotes/components/CostLineInlineRow'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import type { CostLine, CostTotals } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* The line-item editor: the visual twin of the dense table for editable rows.
 *
 * This is the surface with the tightest speed budget in the product. The PRD's
 * explicit counter-signal is that quoting must not be slower than the Excel
 * path it replaces, so: editing is one click on the row, adding never opens a
 * modal from a button hunt, and the add-row sits INSIDE the table at data-row
 * height rather than below it.
 *
 * That last clause described an intention, not the code, from the day this
 * shipped until the inline row arrived: Add opened the same sheet as editing.
 * Then adding went inline and editing kept the sheet, which left the faster
 * path on the rarer action — a line is added once and corrected several times.
 * Both are now inline on desktop: clicking a row turns THAT row into the same
 * form, and each row carries its own delete in a trailing column.
 *
 * `onEdit` survives for the phone, and as the desktop's fallback when no inline
 * handler is wired. On phone the pattern changes rather than shrinking — one
 * line, one sheet. Horizontally scrolling an editable grid is prohibited.
 *
 * Deleting is TWO presses on the trailing column, never one: the first arms the
 * row, the second removes it. A one-click delete beside a click-to-edit row is
 * the mis-click that costs a line and its share of the margin, and an inline
 * row has no room for the sheet's sentence of confirmation.
 *
 * TWO money columns, both on the COST side: the unit price an operator types
 * per charge line, and the amount that price comes to at the quantity beside
 * it. The customer's price is derived from the costing by the markup panel, so
 * it is computed rather than entered and belongs to the footer and the offer,
 * not to a column somebody types into.
 *
 * There were briefly one, and it could not be made honest. Headed "Unit price"
 * it printed the extension, so the heading lied about its own cell; headed
 * "Amount" it held the input, so the figure an operator types sat under the
 * word for the figure nobody types; and printing the unit figure instead broke
 * the column's reconciliation with the footer for every quantity that was not
 * 1. The source workbook has both columns for exactly this reason, and the
 * arithmetic between them is the only thing that makes the footer readable.
 *
 * The customer's total separates from the costing with a 2px ink rule: the one
 * heavy line permitted anywhere in the system, because the boundary between
 * what was entered and what was computed must be unmistakable. Above it is the
 * costing and the margin added to it; below it is what the customer pays. That
 * is the workbook's own argument, in the workbook's own order — this is what
 * the quote costs, this is what is added, this is the price.
 *
 * The customer total under that rule is `totals.sell`, and MarginPanel prints
 * the same value off the same record — the costing tab mounts both, so the two
 * are worded from ONE key (`quotes.costLines.customerTotal`) rather than from a
 * label of their own each. One figure under two labels that had drifted apart
 * would read as two figures.
 *
 * The totals themselves come from the SERVER (FR-1). They are not summed here
 * even though every input is on screen: the margin is what a price is decided
 * on, and two surfaces that each add up the same lines will eventually disagree
 * by a rounding rule. `totals` absent means the response predates the costing
 * endpoint, and the footer falls back to summing rather than showing nothing.
 */

export interface CostLineEditorProps {
  lines: CostLine[]
  /** The server's arithmetic (FR-1). Preferred over anything computed here. */
  totals?: CostTotals
  /** Sales users see amounts on their own deals; only the vendor is hidden. */
  readOnly?: boolean
  /** Opens the sheet for a line, or for a new one. Owned by the section above
   *  so a failed write can keep the sheet open with the draft intact.
   *
   *  This is the PHONE's path for both actions, and the desktop's fallback for
   *  whichever inline handler is absent. */
  onEdit?: (line: CostLine | 'new') => void
  /** Saves a row entered inline. Absent means the desktop add-row is not
   *  offered and the Add button falls back to opening the sheet. */
  onAddInline?: (draft: CostLineInlineDraft, onSaved: () => void) => void
  /** Saves an existing row edited in place. Absent means a desktop row click
   *  falls back to opening the sheet. */
  onEditInline?: (
    line: CostLine,
    draft: CostLineInlineDraft,
    onSaved: () => void,
  ) => void
  /** Removes a line from the trailing column. Absent means no inline delete is
   *  offered and removal stays in the sheet. */
  onDeleteInline?: (line: CostLine) => void
  /** A write is in flight; the inline row locks rather than accepting a second
   *  line that would race the first. */
  pending?: boolean
}

/** The fallback footer, used only when the server sent no totals.
 *
 * Number() on money is exactly what the payload path refuses, and it is
 * tolerable here for the same reason it is not there: this figure is READ, not
 * stored, and a display that is a cent out beats a footer that is blank. The
 * server's totals replace it the moment they arrive. */
function localTotal(lines: CostLine[], side: 'buy' | 'sell'): number {
  return lines.reduce(
    (sum, line) => sum + Number(line[side].amount) * line.quantity,
    0,
  )
}

export function CostLineEditor({
  lines,
  totals,
  readOnly,
  onEdit,
  onAddInline,
  onEditInline,
  onDeleteInline,
  pending,
}: CostLineEditorProps) {
  const { t } = useTranslation()

  /* Whether the desktop add-row is showing. Local because it is pure view
     state: nothing above needs to know, and a failed save keeps the row open
     with what was typed by not clearing it. */
  const [adding, setAdding] = useState(false)

  /* The row being edited in place, and the row armed for deletion — both by id,
     and both local for the same reason as `adding`. Ids rather than the line
     objects so that a refetch between renders cannot leave a stale copy open. */
  const [editingId, setEditingId] = useState<string | null>(null)
  const [armedId, setArmedId] = useState<string | null>(null)

  /* An accepted rate memory (FR-17): the line it was accepted on, and the
     figure to open that line's price field on. Carried as a pair rather than a
     bare amount so it cannot leak onto a different row, and cleared when the
     row closes — a seed that outlived its row would put last quarter's rate
     under the hand of somebody who clicked a line only to read it. */
  const [seed, setSeed] = useState<{ lineId: string; unitPrice: string } | null>(
    null,
  )

  const buy = totals ? Number(totals.buy.amount) : localTotal(lines, 'buy')
  const sell = totals ? Number(totals.sell.amount) : localTotal(lines, 'sell')
  const margin = totals ? Number(totals.margin.amount) : sell - buy
  const marginPercent = totals
    ? Number(totals.marginPercent)
    : sell === 0
      ? 0
      : (margin / sell) * 100

  /** Opens a line for editing: in place where the caller wired an inline
   *  handler, in the sheet otherwise. Arming a delete is dropped on the way in,
   *  so a row cannot be open for editing and waiting to be removed at once. */
  const openLine = (line: CostLine) => {
    setArmedId(null)
    /* A seed for THIS line survives, because accepting a rate memory opens the
       row through this same path — the chip sits inside the row and its click
       reaches the row's handler too. A seed for any other line is dropped. */
    setSeed((current) => (current?.lineId === line.id ? current : null))
    if (onEditInline) {
      setEditingId(line.id)
      return
    }
    onEdit?.(line)
  }

  /** Closes an open row and forgets any figure a rate memory seeded it with. */
  const closeLine = () => {
    setEditingId(null)
    setSeed(null)
  }

  /** Accepting a rate memory (FR-17): opens the line's row on the remembered
   *  figure. CURRIED so the amount is read where the DTO is still narrowed and
   *  bound as a value — the handler closes over a string, not over the line. */
  const acceptRate = (lineId: string, unitPrice: string) => () => {
    setArmedId(null)
    setSeed({ lineId, unitPrice })
    setEditingId(lineId)
  }

  /** A line's extended figure, server-computed where it exists. */
  const extended = (line: CostLine, side: 'buy' | 'sell'): number => {
    const total = side === 'buy' ? line.buyTotal : line.sellTotal
    return total ? Number(total.amount) : Number(line[side].amount) * line.quantity
  }

  return (
    <>
      {/* Desktop: an editable grid. */}
      <div className="hidden md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className="asa-eyebrow h-9 px-[18px]">{t('quotes.costLines.description')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3">{t('quotes.costLines.vendor')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-16 px-3 text-right">{t('quotes.costLines.qty')}</th>
              {/* Two price columns, both on the COST side — the unit price an
                  operator types and the amount it comes to, as in the
                  workbook's INTERNAL COST block. The customer's price per line
                  is derived from the costing by the markup and is not shown
                  here: it is the offer's figure, and repeating it in the
                  columns somebody types into is how the two get confused on the
                  surface where that is most expensive. */}
              <th scope="col" className="asa-eyebrow h-9 w-32 px-3 text-right">{t('quotes.costLines.unitPrice')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-32 px-3 text-right">{t('quotes.costLines.amount')}</th>
              {/* The actions column's header is visually empty — the icons
                  under it say what they do — but it is named for screen
                  readers, as the list tables in this system name theirs. */}
              <th scope="col" className="h-9 w-20 px-3">
                <span className="sr-only">{t('common.actions.label')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line) =>
              /* The open row REPLACES its read row rather than sitting under
                 it, so the table neither grows nor shows the same line twice. */
              !readOnly && onEditInline && editingId === line.id ? (
                <CostLineInlineRow
                  key={line.id}
                  record={line}
                  seedUnitPrice={
                    seed?.lineId === line.id ? seed.unitPrice : undefined
                  }
                  onSubmit={(draft, onSaved) =>
                    onEditInline(line, draft, onSaved)
                  }
                  onCancel={closeLine}
                  pending={pending}
                />
              ) : (
              <tr
                key={line.id}
                onClick={readOnly ? undefined : () => openLine(line)}
                tabIndex={readOnly ? undefined : 0}
                aria-label={readOnly ? undefined : t('quotes.costLines.edit')}
                onKeyDown={
                  readOnly
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          openLine(line)
                        }
                      }
                }
                className={cn(
                  'border-line-hair h-[38px] border-b transition-colors',
                  !readOnly && 'hover:bg-surface-hover group cursor-pointer',
                )}
              >
                <td className="px-[18px]">
                  <div className="flex items-center gap-2">
                    <span className="text-text-heading text-[13px]">
                      {line.description}
                    </span>
                    {/* Rate memory REPORTS; it never pre-fills. Accepting is one
                        click, ignoring costs nothing.
                        The click OPENS the row on the remembered figure rather
                        than writing it: what the lane cost last quarter is a
                        starting point, and the only person who can say whether
                        it still holds is the one looking at this row. The chip
                        was mute while the one price this form collects was the
                        sell — a remembered COST had no field to land in, and a
                        tap that writes an invisible figure is worse than no
                        hint. The price column is the cost side now, so it has
                        its field back.
                        Where no inline handler is wired the chip stays mute in
                        the same way: the click still reaches the row and opens
                        the sheet, which seeds from the line, so nothing is
                        filled behind the reader's back on that path either. */}
                    {line.rateMemory ? (
                      <RateMemoryChip
                        amount={formatNumber(line.rateMemory.amount.amount, 2)}
                        sourceCaseNumber={line.rateMemory.sourceCaseNumber}
                        month={line.rateMemory.month}
                        onAccept={
                          !readOnly && onEditInline
                            ? // The wire's own string (AD-9), read here and not
                              // inside the handler: Number() on a remembered
                              // rate is how it reaches the field as
                              // 1234.5499999999999.
                              acceptRate(line.id, line.rateMemory.amount.amount)
                            : undefined
                        }
                      />
                    ) : null}
                  </div>
                </td>
                <td className="text-text-muted px-3 text-[12.5px]">
                  <SuppressedValue value={line.vendorName} label={t('common.suppressed.vendor')} />
                </td>
                <td className="asa-num text-text-body px-3 text-right text-[13px]">
                  {line.quantity}
                </td>
                {/* The UNIT cost: the figure this row's form holds, printed by
                    the read row so that clicking it opens on the number that
                    was already on screen. */}
                <td className="asa-num text-text-body px-3 text-right text-[13px]">
                  {formatNumber(line.buy.amount, 2)}
                </td>
                {/* The EXTENDED cost — what this line costs at the quantity
                    beside it, and the column the footer's cost row adds up. It
                    carries the heading weight of the two because it is the
                    figure that is summed; the unit price to its left is the
                    figure that is entered. A reader sums this column by eye and
                    must land on the row under the rule. */}
                <td className="asa-num text-text-heading px-3 text-right text-[13px]">
                  {formatNumber(extended(line, 'buy'), 2)}
                </td>

                {/* Delete, in the row, in two presses. The trash is quiet until
                    the row is hovered or focused so a table being READ is a
                    table of figures, not a column of red; the armed state stays
                    visible regardless, because a row waiting for a second press
                    must not vanish when the pointer drifts off it. */}
                <td className="px-3">
                  {!readOnly && onDeleteInline ? (
                    <div className="flex items-center justify-end">
                      {armedId === line.id ? (
                        <span className="flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              onDeleteInline(line)
                              setArmedId(null)
                            }}
                            disabled={pending}
                            aria-label={t('quotes.costLines.deleteConfirmAction')}
                            title={t('quotes.costLines.deleteConfirm')}
                            className="text-negative bg-negative-soft cursor-pointer rounded-[5px] px-1.5 py-0.5 text-[12px] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {t('quotes.costLines.deleteConfirmAction')}
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation()
                              setArmedId(null)
                            }}
                            aria-label={t('common.actions.cancel')}
                            title={t('common.actions.cancel')}
                            className="text-text-muted hover:bg-surface-hover cursor-pointer rounded-[5px] p-1"
                          >
                            <X size={14} aria-hidden />
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(event) => {
                            /* The row itself opens the editor on click; the
                               trash must not do both. */
                            event.stopPropagation()
                            setArmedId(line.id)
                          }}
                          disabled={pending}
                          aria-label={t('quotes.costLines.delete')}
                          title={t('quotes.costLines.delete')}
                          className="text-text-muted hover:text-negative hover:bg-surface-hover cursor-pointer rounded-[5px] p-1 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <Trash2 size={14} aria-hidden />
                        </button>
                      )}
                    </div>
                  ) : null}
                </td>
              </tr>
              ),
            )}

            {/* The add affordance and the row it becomes occupy the same slot,
                so the table does not grow a line when adding starts. */}
            {!readOnly && adding && onAddInline ? (
              <CostLineInlineRow
                onSubmit={onAddInline}
                onCancel={() => setAdding(false)}
                pending={pending}
              />
            ) : null}

            {!readOnly ? (
              <tr className="border-line-hair h-[38px] border-b">
                <td colSpan={6} className="px-[18px]">
                  <button
                    type="button"
                    onClick={() =>
                      onAddInline ? setAdding(true) : onEdit?.('new')
                    }
                    className="text-brand flex cursor-pointer items-center gap-1.5 text-[13px]"
                  >
                    <Plus size={14} aria-hidden />
                    {t('quotes.costLines.add')}
                  </button>
                </td>
              </tr>
            ) : null}
          </tbody>

          {/* Three rows in the workbook's order, all of them landing in the
              Amount column so the footer is read straight down from the figures
              it is made of: what the quote costs, what is added to it, what the
              customer pays. The actions column is left empty rather than
              absorbed into the label, so every figure on this table shares one
              right edge.
              Nothing here is summed on this surface — `buy`, `margin` and
              `sell` are the SERVER's figures wherever the response carried them
              (FR-1), and the local fallbacks only exist so a response that
              predates the costing endpoint shows a footer rather than a
              blank. */}
          <tfoot>
            <tr>
              <td className="text-text-muted px-[18px] pt-2 text-[12.5px]" colSpan={4}>
                {t('quotes.costLines.costTotal')}
              </td>
              <td className="asa-num text-text-heading px-3 pt-2 text-right text-[13px]">
                {formatNumber(buy, 2)}
              </td>
              <td className="px-3" />
            </tr>
            <tr>
              {/* The percentage rides with the label rather than taking a
                  column of its own: it reads as a qualifier on the margin, not
                  as a third figure in a table of money. */}
              <td className="text-text-muted px-[18px] py-1 text-[12.5px]" colSpan={4}>
                {t('quotes.costLines.margin')}{' '}
                <span
                  className={cn(
                    'asa-num',
                    margin < 0 ? 'text-negative' : 'text-text-muted',
                  )}
                >
                  {formatNumber(marginPercent, 1)}%
                </span>
              </td>
              <td
                className={cn(
                  'asa-num px-3 py-1 text-right text-[13px]',
                  margin < 0 ? 'text-negative' : 'text-positive',
                )}
              >
                {formatNumber(margin, 2)}
              </td>
              <td className="px-3" />
            </tr>
            {/* The one heavy rule in the system, and it now falls where the
                argument turns: everything above it is the costing and what is
                added to it, and this row is the price the customer is quoted.
                It carries the semibold weight for the same reason — of the
                three figures, this is the one somebody signs. */}
            <tr className="border-ink border-t-2">
              <td className="text-text-muted px-[18px] pt-2 pb-3 text-[12.5px]" colSpan={4}>
                {t('quotes.costLines.customerTotal')}
              </td>
              <td className="asa-num text-text-heading px-3 pt-2 pb-3 text-right text-[15px] font-semibold">
                {formatNumber(sell, 2)}
              </td>
              <td className="px-3" />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Phone: one line, one card; tapping opens the sheet. */}
      <div className="md:hidden">
        <ul className="flex flex-col">
          {lines.map((line, index) => (
            <li key={line.id}>
              <button
                type="button"
                onClick={readOnly ? undefined : () => onEdit?.(line)}
                className={cn(
                  'w-full px-4 py-3 text-left',
                  index !== lines.length - 1 && 'border-line-hair border-b',
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="flex min-w-0 flex-col">
                    <span className="text-text-heading truncate text-[13px]">
                      {line.description}
                    </span>
                    {/* The quantity alone, and the figure on the right is the
                        extended COST — the same side of the line the desktop
                        column shows and the same side the sheet this card opens
                        collects. Showing the customer's price here instead
                        would put a figure on the card that the form behind it
                        cannot edit. */}
                    <span className="text-text-muted text-[12px] font-light">
                      {t('quotes.costLines.qty')} {line.quantity}
                    </span>
                  </span>
                  <span className="asa-num text-text-heading shrink-0 text-[13px]">
                    {formatNumber(extended(line, 'buy'), 2)}
                  </span>
                </div>
              </button>
            </li>
          ))}
        </ul>

        {/* The phone's footer sums the cards above it, and those cards carry
            extended COSTS — so this is the cost total, under the cost total's
            own name. It used to state the customer's total instead, which is
            the same mismatch the desktop's single money column had: a figure
            that cannot be arrived at from anything on the surface it closes.
            The customer's price is not lost by this — it is on the costing
            header and in the markup panel, both of which are on screen on the
            phone too, and both of which now use these same two words.
            One row, not three: the desktop's argument needs an Amount column to
            line up against, and stacking three figures under a card list would
            be a second totals panel on a surface that already has one. */}
        <div className="border-ink flex items-center justify-between border-t-2 px-4 py-3">
          <span className="text-text-muted text-[12.5px]">{t('quotes.costLines.costTotal')}</span>
          <span className="asa-num text-text-heading text-[15px] font-semibold">
            {formatNumber(buy, 2)}
          </span>
        </div>

        {!readOnly ? (
          <div className="px-4 pb-4">
            <Button
              variant="secondary"
              size="sm"
              iconLeft={Plus}
              className="w-full"
              onClick={() => onEdit?.('new')}
            >
              {t('quotes.costLines.add')}
            </Button>
          </div>
        ) : null}
      </div>
    </>
  )
}
